/**
 * Production-host allowlist for the PostHog capture floor.
 *
 * WHY (2026-07-22, staging-spine activation): staging.bookeper.pl /
 * staging-accounting.bookeper.pl are Vercel PREVIEW deployments of `main`.
 * Vercel env vars scoped to "All Environments" (like the PostHog key) leak
 * into previews, so staging/preview traffic would land in the production
 * PostHog project. Server-side the project filter excludes `staging` /
 * `.vercel.app` hosts from insights, but the honest fix is to never ingest:
 * this allowlist is enforced in both frontends' `before_send`, same layer as
 * the bot and internal-machine floors.
 *
 * Allowlist (not blocklist): any future preview/staging/ephemeral domain is
 * excluded by default — only the three real production surfaces capture.
 *
 * Escape hatch for deliberate local instrumentation tests:
 * NEXT_PUBLIC_POSTHOG_DEV_CAPTURE / VITE_POSTHOG_DEV_CAPTURE = "true"
 * (checked by the callers, not here — this module stays env-free).
 */

const PROD_CAPTURE_HOSTS = new Set([
  'bookeper.pl',
  'www.bookeper.pl',
  'app.bookeper.pl',
  'accounting.bookeper.pl',
]);

/**
 * True when `hostname` is one of the production surfaces that may send
 * analytics to the production PostHog project. Undefined/empty → false
 * (SSR renders nothing capture-worthy; the client re-evaluates).
 */
export function isProdCaptureHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  return PROD_CAPTURE_HOSTS.has(hostname.toLowerCase());
}

/**
 * Hosts whose pages are served by the LANDING Vercel project, including the
 * `apps/frontend` pages it reverse-proxies (`apps/landing/vercel.json`
 * rewrites `/kalkulatory/*`, `/blog/*`, `/zawody/*`, `/koszty/*`,
 * `/analyzer/*` to `https://app.bookeper.pl/...`).
 *
 * WHY THIS EXISTS (2026-09-13, calculator_viewed regression): those rewrites
 * serve the Next app's HTML and JS under the www ORIGIN, so every
 * origin-relative URL that code computes resolves against www.bookeper.pl —
 * including posthog-js's `api_host: "/ingest"`. `/ingest/*` is a Next Route
 * Handler inside `apps/frontend` (`src/app/ingest/[...path]/route.ts`).
 * Landing `vercel.json` rewrites `/ingest/:path*` to that cookie-stripping
 * handler, so first-party ingest is routable on www again. This helper
 * still names the landing-served hosts so ingest/host resolution can treat
 * them as first-party `/ingest` rather than a direct EU fallback.
 *
 * Allowlist, not blocklist: any origin NOT named here is one the Next app
 * serves itself (app.bookeper.pl, staging.bookeper.pl, *.vercel.app previews,
 * localhost), where the `/ingest` route handler exists and the same-origin
 * proxy stays the right choice.
 */
const LANDING_PROXIED_HOSTS = new Set(['bookeper.pl', 'www.bookeper.pl']);

/**
 * True when `hostname` is served by the landing project, i.e. an
 * `apps/frontend` page reached it through a `vercel.json` rewrite. Same-origin
 * `/ingest` on these hosts now rewrites to the app cookie-stripping proxy.
 *
 * Undefined/empty → false: SSR has no origin to judge, and the client
 * re-evaluates.
 */
export function isLandingProxiedHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  return LANDING_PROXIED_HOSTS.has(hostname.toLowerCase());
}

/**
 * Transient redirects never represent analytics pageviews, including descendants.
 * The optional leading locale segment matches frontend i18n/routing.ts (pl/en/uk,
 * as-needed); a similarly named article or an unsupported prefix is not a route.
 */
export function isSensitiveAnalyticsPathname(pathname: string): boolean {
  const route = pathname.replace(/^\/(?:pl|en|uk)(?=\/)/, '');
  return ['/auth/callback', '/signing/callback', '/auth/auth-code-error'].some(
    (prefix) => route === prefix || route.startsWith(prefix + '/')
  );
}
